import React from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid
} from '@mui/material';
import SEOHead from '../components/SEO/SEOHead';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

const NotFound = () => {
  return (
    <>
      <SEOHead
        title="Page Not Found - 404 Error | VerveAI"
        description="The page you're looking for doesn't exist. Return to VerveAI homepage or explore our startup launchpad packages."
        canonical="https://verveai.co/404"
        noIndex={true}
      />
      
      <Container maxWidth="md" sx={{ py: 12, textAlign: 'center' }}>
        {/* 404 Visual */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '8rem', md: '12rem' },
              fontWeight: 800,
              background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: 0.8,
              mb: 2
            }}
          >
            404
          </Typography>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: 'text.primary'
            }}
          >
            Page Not Found
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}
          >
            Oops! The page you're looking for seems to have vanished into the digital void. 
            Don't worry, even our AI can't predict every URL.
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Grid container spacing={2} justifyContent="center" sx={{ mb: 6 }}>
          <Grid item xs={12} sm={4}>
            <Button
              component={Link}
              to="/"
              variant="contained"
              size="large"
              startIcon={<HomeIcon />}
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                py: 1.5,
                '&:hover': {
                  background: 'linear-gradient(135deg, #F7931E 0%, #FF6B35 100%)',
                }
              }}
            >
              Go Home
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              component="a"
              href="/#packages"
              variant="outlined"
              size="large"
              startIcon={<SearchIcon />}
              fullWidth
              sx={{
                borderColor: '#FF6B35',
                color: '#FF6B35',
                py: 1.5,
                '&:hover': {
                  borderColor: '#F7931E',
                  backgroundColor: 'rgba(255, 107, 53, 0.1)'
                }
              }}
            >
              Launchpad Packages
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              component={Link}
              to="/contact"
              variant="outlined"
              size="large"
              startIcon={<ContactSupportIcon />}
              fullWidth
              sx={{
                borderColor: '#00E676',
                color: '#00E676',
                py: 1.5,
                '&:hover': {
                  borderColor: '#00E676',
                  backgroundColor: 'rgba(0, 230, 118, 0.1)'
                }
              }}
            >
              Get Help
            </Button>
          </Grid>
        </Grid>

        {/* Helpful Links */}
        <Box sx={{ p: 4, borderRadius: 2, background: 'rgba(255, 107, 53, 0.1)' }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Popular Pages
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Button component={Link} to="/blog" color="inherit" size="small">
                Blog
              </Button>
            </Grid>
            <Grid item>
              <Button component={Link} to="/launchpad" color="inherit" size="small">
                Startup Launchpad
              </Button>
            </Grid>
            <Grid item>
              <Button component={Link} to="/about" color="inherit" size="small">
                About Us
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Fun Message */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 4, fontStyle: 'italic' }}
        >
          "In the world of AI, even 404 errors are learning experiences." - VerveAI Team
        </Typography>
      </Container>
    </>
  );
};

export default NotFound;